/**
 * The behaviour half of a toast: how you hold one, how you throw one away,
 * and whose voice it is in.
 *
 * The presentational half lives in `@gramas/ui`, which knows nothing about
 * her. Everything here that would look out of place in a component library —
 * her mark, her arrival animation, her sound — is here rather than there.
 *
 * The gestures follow the sheet's language rather than inventing one: a drag
 * past a threshold commits, a flick commits earlier, and anything shorter than
 * a few pixels was a tap. The one non-obvious decision is that a tap *holds*
 * rather than dismisses. A toast you can accidentally destroy by touching it
 * while reading is a toast you learn not to touch, and the whole point of
 * putting a finger on this one is that it stops the clock.
 */

import { Toast, type ToastTone } from '@gramas/ui'
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react'
import { GlitimeMark } from '../glitime/Mark.js'
import { petHer } from '../glitime/pet.js'
import { playCue } from '../sound/engine.js'
import { GhostReveal } from '../stage/GhostReveal.js'
import { dismiss, pressEnd, pressStart, type ToastItem, toggleHold } from './toastStore.js'

/** Past this the finger is dragging, not resting. */
const DRAG_START_PX = 8
/** Past this, letting go throws it away. */
const COMMIT_PX = 72
/** A fast flick commits early; below this it is a slow drag and must travel. */
const FLICK_PX_PER_MS = 0.4
const FLICK_MIN_PX = 26

const TONE_BY_KIND: Record<ToastItem['kind'], ToastTone> = {
  utility: 'utility',
  persona: 'persona',
  error: 'danger',
  trophy: 'persona',
}

export function ToastCard({ item }: { item: ToastItem }) {
  const [dragX, setDragX] = useState(0)
  const start = useRef<{ x: number; y: number; at: number } | null>(null)
  const dragging = useRef(false)
  const persona = item.kind === 'persona' || item.kind === 'trophy'

  // Fired on arrival rather than on enqueue: a toast that waited behind a
  // performance should sound when it is heard. The engine itself downloads
  // nothing until a cue actually plays.
  useEffect(() => {
    if (item.cue != null) playCue(item.cue)
  }, [item.cue])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    start.current = { x: event.clientX, y: event.clientY, at: Date.now() }
    dragging.current = false
    pressStart(item.id)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const from = start.current
    if (from == null) return
    const dx = event.clientX - from.x
    const dy = event.clientY - from.y
    if (!dragging.current) {
      if (Math.abs(dx) < DRAG_START_PX) return
      // Axis lock: a mostly-vertical movement belongs to the page underneath,
      // which is scrolling a diary the toast happens to be sitting on top of.
      if (Math.abs(dx) <= Math.abs(dy)) {
        start.current = null
        pressEnd(item.id)
        return
      }
      dragging.current = true
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    setDragX(dx)
  }

  const finish = (event: ReactPointerEvent<HTMLDivElement>) => {
    const from = start.current
    start.current = null
    if (from == null) return
    const dx = event.clientX - from.x
    const travelled = Math.abs(dx)
    const speed = travelled / Math.max(1, Date.now() - from.at)

    if (dragging.current) {
      dragging.current = false
      const flung = speed > FLICK_PX_PER_MS && travelled > FLICK_MIN_PX
      if (flung || travelled > COMMIT_PX) {
        // Carry on in the direction it was thrown rather than snapping back.
        setDragX(dx > 0 ? 420 : -420)
        setTimeout(() => dismiss(item.id), 140)
        return
      }
      setDragX(0)
      pressEnd(item.id)
      return
    }

    // Never moved: a tap. Hold it, or let a held one go.
    toggleHold(item.id)
  }

  const onPointerCancel = () => {
    start.current = null
    dragging.current = false
    setDragX(0)
    pressEnd(item.id)
  }

  // A pet on the mark itself is a separate gesture from the card's own
  // hold-to-read one: the card's tap turns into `toggleHold` on release,
  // which would leave the toast pinned open (or, on a second pet, resume it
  // early) instead of the plain pause-then-resume a pet is meant to be. So
  // this stops the gesture at the mark rather than letting it reach the
  // card, and pauses/resumes the timer directly with the same primitives the
  // card's own drag uses — no new timer machinery, just a second door to it.
  const petMarkDown = (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    pressStart(item.id)
  }
  const petMarkUp = (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    pressEnd(item.id)
  }

  return (
    <Toast
      tone={TONE_BY_KIND[item.kind]}
      alert={item.kind === 'error'}
      // A trophy arrives the way she arrives on the stage — word by word, with
      // one pass of light. This is the effect the old `reveal` flag was meant
      // to produce; it applied a class that existed nowhere, so every trophy
      // in the app so far has looked exactly like a confirmation of a meal.
      message={
        item.kind === 'trophy' ? <GhostReveal sweep>{item.message}</GhostReveal> : item.message
      }
      leading={
        persona ? (
          <span
            onPointerDown={petMarkDown}
            onPointerUp={petMarkUp}
            onPointerLeave={petMarkUp}
            onPointerCancel={petMarkUp}
          >
            <GlitimeMark size={20} alive onPet={petHer} className="mr-0.5" station="toast" />
          </span>
        ) : undefined
      }
      leaving={item.leaving}
      held={item.held}
      dragX={dragX}
      actionLabel={item.action?.label}
      onAction={
        item.action
          ? () => {
              void item.action?.run()
              dismiss(item.id)
            }
          : undefined
      }
      chips={item.chips?.map((chip) => ({
        label: chip.label,
        onSelect: () => {
          dismiss(item.id)
          void chip.run()
        },
      }))}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={onPointerCancel}
    />
  )
}
